<template>
	<div
		v-if="domain"
		id="login-panel"
		class="login-page"
		:class="`login-position-${loginPosition}`"
		:style="backgroundStyle"
	>
		<div class="login-backdrop" aria-hidden="true"></div>
		<main class="login-card">
			<div class="login-card__accent" aria-hidden="true"></div>
			<v-container class="login-card__content" v-if="isAccountPaid && !isStarterAccount">
				<v-form v-model="isValid" @submit.prevent>
					<div class="login-brand">
						<img :src="logo" alt="" class="login-logo" />
					</div>

					<section v-if="view === 'init'" class="login-form" aria-live="polite">
						<div
							v-html="domain.customBranding && domain.customBranding.loginGreeting"
							class="login-greeting"
						></div>

						<button
							v-if="entryScreen === 'start'"
							type="button"
							class="login-method"
							@click="magicLinkMode()"
						>
							<span class="login-method__icon">
								<portal-icon name="sparkles" :size="20"></portal-icon>
							</span>
							<span>{{ $t('login.use-magic-link') }}</span>
							<portal-icon name="arrow-right" :size="17" class="login-method__arrow"></portal-icon>
						</button>

						<div v-if="entryScreen === 'start'" class="login-divider">
							<span>{{ $t('login.or-password') }}</span>
						</div>

						<div class="login-fields">
							<v-text-field
								outlined
								persistent-placeholder
								hide-details="auto"
								autocomplete="email"
								v-model="email"
								:label="$t('login.email')"
								:rules="emailRules"
							></v-text-field>
							<v-text-field
								v-if="entryScreen === 'start'"
								type="password"
								outlined
								persistent-placeholder
								hide-details="auto"
								autocomplete="current-password"
								v-model="password"
								:label="$t('login.password')"
								:rules="passwordRules"
								@keyup.enter="isValid && handleFormSubmit()"
							></v-text-field>
						</div>

						<div v-if="errorMsg" class="login-error" role="alert">
							<portal-icon name="alert-circle" :size="18"></portal-icon>
							<span>{{ errorMsg }}</span>
						</div>

						<v-btn
							class="login-submit"
							elevation="0"
							color="primary"
							block
							:disabled="!isValid"
							@click="handleFormSubmit"
						>
							{{ $t('login.sign-in-button') }}
							<portal-icon name="arrow-right" :size="17" class="ml-2"></portal-icon>
						</v-btn>

						<v-checkbox
							class="login-remember"
							v-model="rememberEmail"
							:label="$t('login.remember-email')"
							hide-details
							dense
							:value="true"
						></v-checkbox>

						<button
							v-if="entryScreen !== 'start'"
							type="button"
							class="login-back"
							@click="entryScreen = 'start'"
						>
							<portal-icon name="arrow-left" :size="18"></portal-icon>
							<span>{{ $t('login.all-options') }}</span>
						</button>
					</section>

					<section v-else-if="view === 'email-sent'" class="login-success" aria-live="polite">
						<span class="login-success__icon">
							<portal-icon name="circle-check" :size="28"></portal-icon>
						</span>
						<p>{{ $t('login.magic-link') }} <strong>{{ email }}</strong>. {{ $t('login.click-link') }}</p>
					</section>
				</v-form>
			</v-container>
			<v-container class="login-card__content" v-else>
				<div class="login-unavailable">
					<span class="login-success__icon">
						<portal-icon name="alert-circle" :size="28"></portal-icon>
					</span>
					<h1>{{ $t('login.feature-not-enabled') }}</h1>
				</div>
			</v-container>
		</main>
	</div>
</template>

<script>
	import AuthService from '@/modules/auth/AuthService';
	import { portalLogo, resolvePortalBranding } from '@/utils/portalBranding';

	export default {
		name: 'Login',

		props: [],

		components: {},

		data: function() {
			return {
				isValid: true,
				rememberEmail: false,

				errorMsg: null,
				view: 'init',
				disabled: false,
				entryScreen: 'start',

				email: null,
				password: null,
				emailRules: [(v) => !!v || this.$t('valid.email-required'), (v) => /.+@.+\..+/.test(v) || this.$t('valid.email')],
				passwordRules: [
					(v) => !!v || '',
					(v) => (v && v.length >= 8) || '',
				],

			};
		},

		mounted() {
			if (this.$store.state.isLoggedIn) {
				this.$router.push('/welcome');
			}

			try {
				let savedEmail = localStorage.getItem('login-email');

				if (!this.$validations.isEmpty(savedEmail)) {
					this.email = savedEmail;
					this.rememberEmail = true;
				}
			} catch (err) {
				console.log('Error reading preferences from local storage.');
			}
		},

		beforeDestroy() {},

		methods: {
			magicLinkMode: function() {
				this.entryScreen = 'magic';
			},

			handleFormSubmit() {
				if (this.rememberEmail) {
					try {
						console.log('writing email to local storage');
						localStorage.setItem('login-email', this.email.trim());
					} catch (err) {
						console.log('Error putting preferences into local storage.');
					}
				} else {
					localStorage.removeItem('login-email');
				}

				if (this.entryScreen === 'magic') {
					this.requestMagicLink();
				} else {
					this.authWithCredentials();
				}
			},

			authWithCredentials() {
				this.$store.commit('startLoading');
				AuthService.authWithCredentials(this.$store.getters.getAccountId, this.email.trim(), this.password)
					.then(() => {
						self.location.href = document.location.protocol + '//' + document.location.host;
					})
					.catch((err) => {
						this.errorMsg = this.$t('error.bad-credentials') + ' [' + (err.response ? err.response.status : 'Unknown') + ']';
					})
					.finally(() => {
						this.$store.commit('stopLoading');
					});
			},

			requestMagicLink() {
				this.$store.commit('startLoading');
				this.errorMsg = null;
				this.disabled = true;

				let url = document.location.protocol + '//' + document.location.host;
				url = url + '/magicToken';

				if (this.$store.state.origRequestedUri) {
					url = url + '?origUri=' + this.$store.state.origRequestedUri;
				} else {
					url = url + '?origUri=/welcome';
				}

				AuthService.requestMagicLink(this.$store.getters.getAccountId, this.email.trim(), url)
					.then(() => {
						this.view = 'email-sent';
					})
					.catch((err) => {
            this.errorMsg = this.$t('error.unable-to-locate') + ' [' + (err.response ? err.response.status : 'Unknown') + ']';
					})
					.finally(() => {
						this.$store.commit('stopLoading');
					});
			},
		},

		computed: {
			logo: function() {
				const branding = resolvePortalBranding(this.$store.state.domain, this.$vuetify.theme.dark ? 'dark' : 'light');
				if (branding) {
					return portalLogo(
						branding,
						this.$vuetify.theme.dark
							? this.$store.state.domain.logoDark || this.$store.state.domain.logo
							: this.$store.state.domain.logo,
						branding.colors.surface
					);
				}
				if (this.$vuetify.theme.dark && this.$store.state.domain.logoDark) {
					return this.$store.state.domain.logoDark;
				} else {
					return this.$store.state.domain.logo;
				}
			},

			isAccountPaid: function() {
				if (this.$store.state.domain) {
					return this.$store.state.domain.isAccountPaid;
				} else {
					return false;
				}
			},

      isStarterAccount: function() {
        if (this.$store.state.domain) {
          return this.$store.state.domain.isStarterAccount;
        } else {
          return false;
        }
      },

			domain: function() {
				return this.$store.state.domain;
			},

			backgroundStyle: function() {
				const splash = this.domain.customBranding && this.domain.customBranding.loginSplash;
				return splash
					? {
							backgroundImage: `linear-gradient(135deg, rgba(16, 18, 26, 0.36), rgba(16, 18, 26, 0.16)), url("${splash}")`,
						}
					: {};
			},

			loginPosition: function() {
				return String(
					(this.domain.customBranding && this.domain.customBranding.loginBoxPosition) || 'Center'
				).toLowerCase();
			},
		},

		watch: {},
	};
</script>

<style scoped lang="scss">
	.login-page {
		position: relative;
		isolation: isolate;
		width: 100%;
		min-height: 100vh;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: clamp(24px, 5vw, 72px);
		overflow: hidden;
		background:
			radial-gradient(circle at 15% 15%, color-mix(in srgb, var(--v-primary-base) 22%, transparent), transparent 34%),
			linear-gradient(135deg, var(--v-gray_10-base), color-mix(in srgb, var(--v-primary-base) 9%, var(--v-gray_10-base)));
		background-size: cover;
		background-position: center;
	}

	.login-backdrop {
		position: absolute;
		inset: 0;
		z-index: -1;
		background:
			linear-gradient(135deg, rgba(255, 255, 255, 0.04), transparent 50%),
			radial-gradient(circle at 85% 20%, rgba(255, 255, 255, 0.16), transparent 28%);
		pointer-events: none;
	}

	.login-position-left {
		justify-content: flex-start;
	}

	.login-position-right {
		justify-content: flex-end;
	}

	.login-card {
		position: relative;
		width: min(456px, 100%);
		max-height: calc(100vh - 48px);
		overflow-y: auto;
		border: 1px solid rgba(255, 255, 255, 0.68);
		border-radius: var(--brand-surface-radius, 24px);
		background: rgba(255, 255, 255, 0.96);
		color: #252329;
		box-shadow:
			0 28px 80px rgba(17, 20, 31, 0.24),
			0 4px 16px rgba(17, 20, 31, 0.08);
		backdrop-filter: blur(20px);

		&__accent {
			height: 4px;
			background: linear-gradient(90deg, var(--v-primary-base), color-mix(in srgb, var(--v-primary-base) 55%, #ffffff));
		}

		&__content {
			max-width: none;
			padding: clamp(28px, 4vw, 42px) !important;
		}
	}

	.login-brand {
		display: flex;
		justify-content: center;
		margin-bottom: 24px;
	}

	.login-logo {
		display: block;
		max-width: 230px;
		max-height: 82px;
		object-fit: contain;
	}

	.login-form {
		text-align: left;
	}

	.login-greeting {
		margin: 0 auto 26px;
		color: #68636d;
		font-size: 14px;
		line-height: 1.65;
		text-align: center;
		white-space: pre-wrap;

		::v-deep h1,
		::v-deep h2,
		::v-deep h3 {
			margin: 0 0 8px;
			color: #252329;
			font-size: clamp(23px, 4vw, 29px);
			font-weight: 700;
			line-height: 1.22;
			letter-spacing: -0.025em;
		}

		::v-deep p {
			margin: 0;
		}
	}

	.login-method {
		width: 100%;
		min-height: 50px;
		display: flex;
		align-items: center;
		gap: 11px;
		padding: 7px 12px 7px 8px;
		border: 1px solid #dedbe1;
		border-radius: 14px;
		background: #ffffff;
		color: #302d33;
		font: inherit;
		font-size: 14px;
		font-weight: 650;
		text-align: left;
		cursor: pointer;
		transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;

		&:hover {
			border-color: color-mix(in srgb, var(--v-primary-base) 38%, #dedbe1);
			background: color-mix(in srgb, var(--v-primary-base) 3%, #ffffff);
			box-shadow: 0 6px 18px rgba(26, 22, 32, 0.06);
		}

		&:focus-visible {
			outline: 3px solid color-mix(in srgb, var(--v-primary-base) 24%, transparent);
			outline-offset: 2px;
		}

		&__icon {
			width: 36px;
			height: 36px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex: 0 0 auto;
			border-radius: 11px;
			background: color-mix(in srgb, var(--v-primary-base) 9%, #ffffff);
			color: var(--v-primary-base);
		}

		&__arrow {
			margin-left: auto;
			color: #8a858e;
		}
	}

	.login-divider {
		display: flex;
		align-items: center;
		gap: 12px;
		margin: 24px 0;
		color: #8a858e;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;

		&::before,
		&::after {
			content: '';
			height: 1px;
			flex: 1 1 auto;
			background: #e7e4e8;
		}
	}

	.login-fields {
		display: grid;
		gap: 13px;
	}

	.login-fields ::v-deep {
		.v-input__slot {
			min-height: 50px !important;
			border-radius: 13px !important;
			background: #ffffff !important;
		}

		.v-text-field--outlined fieldset {
			border-color: #dcd9df;
		}

		.v-text-field--outlined.v-input--is-focused fieldset {
			border-width: 1.5px;
			border-color: var(--v-primary-base);
		}

		.v-label {
			color: #757079;
			font-size: 14px;
		}

		input {
			color: #252329;
			font-size: 15px;
		}

		.v-messages {
			padding: 4px 4px 0;
		}
	}

	.login-error {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin-top: 13px;
		padding: 11px 12px;
		border: 1px solid color-mix(in srgb, var(--v-error-base) 24%, transparent);
		border-radius: 12px;
		background: color-mix(in srgb, var(--v-error-base) 7%, #ffffff);
		color: var(--v-error-base);
		font-size: 13px;
		line-height: 1.45;

		svg {
			flex: 0 0 auto;
			margin-top: 1px;
		}
	}

	.login-submit {
		min-height: 50px !important;
		margin-top: 16px;
		border-radius: 14px !important;
		font-size: 14px !important;
		font-weight: 700 !important;
		letter-spacing: 0 !important;
		text-transform: none !important;
		box-shadow: 0 10px 24px color-mix(in srgb, var(--v-primary-base) 22%, transparent) !important;

		&.v-btn--disabled {
			box-shadow: none !important;
		}
	}

	.login-remember {
		margin-top: 10px;
		padding-top: 0;

		::v-deep .v-label {
			color: #68636d;
			font-size: 13px;
		}
	}

	.login-back {
		min-height: 38px;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		margin-top: 14px;
		padding: 0 8px;
		border: 0;
		border-radius: 9px;
		background: transparent;
		color: #68636d;
		font: inherit;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;

		&:hover {
			background: #f3f1f4;
			color: #252329;
		}
	}

	.login-success,
	.login-unavailable {
		padding: 12px 0 6px;
		text-align: center;
	}

	.login-success__icon {
		width: 58px;
		height: 58px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 18px;
		border-radius: 18px;
		background: color-mix(in srgb, var(--v-primary-base) 9%, #ffffff);
		color: var(--v-primary-base);
	}

	.login-success p {
		margin: 0;
		color: #68636d;
		font-size: 15px;
		line-height: 1.65;

		strong {
			color: #252329;
			font-weight: 650;
		}
	}

	.login-unavailable h1 {
		margin: 0;
		color: #252329;
		font-size: 21px;
		line-height: 1.35;
	}

	@media (max-width: 600px) {
		.login-page {
			align-items: stretch;
			justify-content: stretch;
			padding: 0;
			background-position: center;
		}

		.login-card {
			width: 100%;
			max-height: none;
			min-height: 100vh;
			border: 0;
			border-radius: 0;
			background: rgba(255, 255, 255, 0.97);
			box-shadow: none;

			&__content {
				max-width: 440px;
				margin: auto;
				padding: 36px 22px !important;
			}
		}

		.login-brand {
			margin-bottom: 28px;
		}

		.login-logo {
			max-height: 72px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.login-method {
			transition: none;
		}
	}
</style>
